// Shared command helpers: context, errors and row constructors used by every command module.
import { newId } from './ids.ts';
import type { Put, WorkspaceView } from './changeset.ts';
import type { Person, Policy, RowTypes, TableName, TimelineType } from './schema.ts';
import type { Day } from './dates.ts';

export interface Ctx {
  now: string;        // ISO timestamp
  today: Day;         // advisor-local calendar day
  actor: string;      // user / device id for audit
  region?: string;
}

export class DomainError extends Error {
  code: string;
  constructor(code: string, message?: string) { super(message ?? code); this.code = code; }
}

// --- helpers -------------------------------------------------------------------
export function created<T extends TableName>(ctx: Ctx, fields: Omit<RowTypes[T], 'id' | 'createdAt' | 'updatedAt' | 'version'> & { id?: string }): RowTypes[T] {
  // an explicit `id: undefined` (forms that create-or-update) must not erase the new id (0.5 fix: new policies)
  const { id, ...rest } = fields as typeof fields & { id?: string };
  return { id: id ?? newId(), createdAt: ctx.now, updatedAt: ctx.now, version: 1, ...rest } as unknown as RowTypes[T];
}
export function touched<T extends { version: number; updatedAt: string }>(ctx: Ctx, row: T, patch: Partial<T>): T {
  return { ...row, ...patch, updatedAt: ctx.now, version: (row.version || 0) + 1 };
}
export function put<T extends TableName>(table: T, row: RowTypes[T]): Put { return { table, row } as Put; }
/** v13: a policy that may still be changed — not found / soft-deleted policies are refused (restore first) */
export function livePolicy(v: WorkspaceView, id: string | null | undefined): Policy {
  const p = v.get('policies', id);
  if (!p) throw new DomainError('policy.not_found');
  if (p.deletedAt) throw new DomainError('policy.deleted');
  return p;
}
export function clean(s: unknown): string { return String(s ?? '').trim(); }
export function opt(s: unknown): string | null { const v = clean(s); return v || null; }

export function timeline(ctx: Ctx, personId: string | null | undefined, type: TimelineType, title: string, body?: string | null, ref?: { table: TableName; id: string }): Put {
  return put('timeline', created<'timeline'>(ctx, { personId: personId ?? null, at: ctx.now, type, title, body: body ?? null, refTable: ref?.table ?? null, refId: ref?.id ?? null }));
}
export function requirePerson(v: WorkspaceView, id: string): Person {
  const p = v.get('persons', id);
  if (!p) throw new DomainError('person.not_found');
  return p;
}

